import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useFormikContext } from "formik";
import cx from "classnames";
import { useBreakpoints } from "@qgiv/core-react";
import { QuantityField } from "@qgiv/core-donor";
import ConnectedSubmit from "./ConnectedSubmit";
import ConnectedProductLimitMessage from "./ConnectedProductLimitMessage";

const ProductQuantity = ({
    displayQuantityInput,
    entityReplacedPurchaseLimitMessage,
    fieldDataId = "",
    hasLimits,
    hasInventory,
    Product_Quantity,
    purchaseLimit,
    quantityLabel,
    qtyAvail,
}) => {
    const { setFieldValue } = useFormikContext();
    const { isSmallScreen } = useBreakpoints();

    const [minusDisabled, setMinusDisabled] = useState(false); // Value === 1
    const [plusDisabled, setPlusDisabled] = useState(false); // value === purchaseLimit

    // Limit max has been reached
    const [limitReached, setLimitReached] = useState(false);
    // Used to display limit message in red when a number over limit is typed into input
    const [overLimit, setOverLimit] = useState(false);

    const [displayInventoryMessage, setDisplayInventoryMessage] =
        useState(false);

    useEffect(() => {
        if (Product_Quantity > 0 && Product_Quantity < purchaseLimit) {
            // Reset all values to false since we are within the range that
            // has no special settings
            setLimitReached(false);
            setOverLimit(false);
            setPlusDisabled(false);
        } else if (Product_Quantity > purchaseLimit) {
            // User has typed in number greater than the limit
            // Trigger the over limit styling and set the number to equal limit
            setOverLimit(true);
            setFieldValue(
                "Product_Quantity",
                purchaseLimit > 0 ? purchaseLimit : Product_Quantity,
            );
        } else if (
            Product_Quantity === purchaseLimit ||
            (Product_Quantity === qtyAvail && qtyAvail < purchaseLimit)
        ) {
            // Since we are setting the number back to the limit if over the limit
            // We don't want to override that styling
            if (!overLimit) {
                setLimitReached(true);
            }

            setPlusDisabled(true);
        }
        setMinusDisabled(Product_Quantity === 1);

        // If user input a number greater than the inventory left we
        // should set the qty to the qtyAvail and display an error
        if (hasInventory && qtyAvail !== 0 && Product_Quantity >= qtyAvail) {
            setDisplayInventoryMessage(true);
            // Inventory needs to respect Purchase limit, when number greater than both inventory and purchase limit is entered
            // We need to reset that number to whichever is the lowest number
            setFieldValue(
                "Product_Quantity",
                // eslint-disable-next-line no-nested-ternary
                (hasInventory && qtyAvail > 0 && !hasLimits) ||
                    (hasLimits && purchaseLimit > qtyAvail)
                    ? qtyAvail
                    : purchaseLimit <= qtyAvail
                      ? purchaseLimit
                      : Product_Quantity,
            );
            // clear inventory message
        } else if (!hasInventory || Product_Quantity < qtyAvail) {
            setDisplayInventoryMessage(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Product_Quantity, qtyAvail, fieldDataId]);

    useEffect(() => {
        setFieldValue("Field_Data_Id", fieldDataId || "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldDataId]);

    const handleChange = (e) => {
        const {
            target: { value },
        } = e;

        setFieldValue(
            "Product_Quantity",
            Number(value) > 0 ? Number(value) : 1,
        );
    };

    const handleIncrementBtnClick = (e) => {
        e.preventDefault();

        if (!purchaseLimit || Product_Quantity < purchaseLimit) {
            setFieldValue("Product_Quantity", Product_Quantity + 1);
        }
    };

    const handleDecrementBtnClick = (e) => {
        e.preventDefault();
        if (Product_Quantity && Product_Quantity > 1) {
            setFieldValue("Product_Quantity", Product_Quantity - 1);
            if (overLimit && Product_Quantity <= purchaseLimit) {
                setOverLimit(false);
            }
            if (limitReached && Product_Quantity < purchaseLimit) {
                setLimitReached(false);
            }
        }
    };
    // eslint-disable-next-line no-nested-ternary
    const disabledPlusReason = displayInventoryMessage
        ? `Only ${qtyAvail} left in Stock`
        : limitReached || overLimit
          ? entityReplacedPurchaseLimitMessage
          : "";

    const minusAriaLabel = minusDisabled
        ? `Quantity Decrease Disabled: Quantity can not be less than one`
        : `Decrease Quantity by one`;
    const plusAriaLabel =
        plusDisabled || displayInventoryMessage
            ? `Quantity Increase Disabled: ${disabledPlusReason}`
            : `Increase Quantity from ${Product_Quantity} to ${
                  Number(Product_Quantity) + 1
              }`;

    const quantityFieldProps = {
        handleIncrementBtnClick,
        handleDecrementBtnClick,
        onChange: handleChange,
        displayInventoryMessage,
        minusDisabled,
        minusAriaLabel,
        plusDisabled,
        plusAriaLabel,
        name: "Product_Quantity",
        value: Product_Quantity,
    };
    return (
        <div className="grid">
            {displayQuantityInput ? (
                <>
                    <div className="col col--12 -margin-bottom--5">
                        <strong className="-color--system-medium-dark">
                            {quantityLabel}
                        </strong>
                    </div>

                    <div className="col col--12 col--md-6 col--no-top-gutter">
                        <QuantityField {...quantityFieldProps} />
                    </div>
                </>
            ) : null}
            {hasLimits && !displayQuantityInput && (
                <ConnectedProductLimitMessage
                    limitReached={limitReached}
                    overLimit={overLimit}
                />
            )}
            <div
                className={cx(
                    "col col--12 ",
                    !isSmallScreen && "col--no-top-gutter",
                    displayQuantityInput && "col--md-6",
                )}
            >
                <ConnectedSubmit />
            </div>
            {hasLimits && displayQuantityInput && (
                <ConnectedProductLimitMessage
                    limitReached={limitReached}
                    overLimit={overLimit}
                />
            )}

            {displayInventoryMessage && (
                <div className="col col--12 -padding-bottom--5 -padding-top--0">
                    <small>
                        <strong className="-color--system-error">{`Only ${qtyAvail} left in Stock`}</strong>
                    </small>
                </div>
            )}
        </div>
    );
};

ProductQuantity.propTypes = {
    displayQuantityInput: PropTypes.bool.isRequired,
    entityReplacedPurchaseLimitMessage: PropTypes.string.isRequired,
    hasInventory: PropTypes.bool.isRequired,
    hasLimits: PropTypes.bool.isRequired,
    quantityLabel: PropTypes.string.isRequired,
    fieldDataId: PropTypes.string,
    Product_Quantity: PropTypes.number.isRequired,
    purchaseLimit: PropTypes.number.isRequired,
    qtyAvail: PropTypes.number.isRequired,
};

export default ProductQuantity;
